import React, { useState } from "react";
import dayjs from "dayjs";
import OrganizerPillSelect from "../../components/OrganizerPillSelect";
import { whatsappHref, WhatsappLink } from "../../components/Whatsapp";
import {
  GRID_SLOT_HEIGHT_PX,
  formatAxisMinute,
  gridItemStyle,
  gridRowStyle,
  minuteToOffset,
} from "../../reservas/components/dailyCourtGridLayout";
import {
  dentroDeTramo,
  estadoVisible,
  filaLibre,
  minutosPreparacion,
  nombreCliente,
  serviciosLabel,
  statusMeta,
} from "./agendaLayout";
import "../../reservas/components/OrganizerDailyCourtGrid.css";
import SerieChip from "./SerieChip";
import "./agenda.css";

/* ─────────────────────────────────────────
   BeautyAgendaGrid — una columna por profesional. Reutiliza el CSS y los helpers de la grilla de
   canchas (`odcg-*`), pero arma sus propias filas: cada columna tiene su horario de trabajo.
───────────────────────────────────────── */

const rango = (item) => `${formatAxisMinute(item.startMin)} a ${formatAxisMinute(item.endMin)}hs`;
const rangoCorto = (item) => `${formatAxisMinute(item.startMin)}-${formatAxisMinute(item.endMin)}`;

// Under 16px the tail cannot hold text (the aria-label still names it); under 24px only the short label fits.
const PREP_ALTURA_MINIMA_PX = 16;
const PREP_ALTURA_COMPLETA_PX = 24;

/** Time the professional stays busy after the service. Not clickable: the free cells under it are not offered. */
function PreparacionCola({ item, style }) {
  const minutos = minutosPreparacion(item);
  const meta = statusMeta(estadoVisible(item));
  const alto = Number(style.height) || 0;
  let texto = null;
  if (alto >= PREP_ALTURA_COMPLETA_PX) texto = `Preparación · ${minutos} min`;
  else if (alto >= PREP_ALTURA_MINIMA_PX) texto = `Prep. ${minutos} min`;
  return (
    <div
      className={`odcg-prep odcg-tone--${meta.tone}`}
      style={style}
      role="img"
      aria-label={`Preparación, ${minutos} minutos`}
    >
      {texto}
    </div>
  );
}

function TurnoCard({ item, style, seleccionado, onSelect }) {
  const meta = statusMeta(estadoVisible(item));
  const servicios = serviciosLabel(item);
  const conWhatsapp = Boolean(whatsappHref(item.phone));
  return (
    <div
      className={`odcg-item odcg-reservation odcg-tone--${meta.tone}${meta.tone === "present" ? " odcg-item--present" : ""}${conWhatsapp ? " odcg-item--has-wa" : ""}${minutosPreparacion(item) > 0 ? " odcg-item--with-prep" : ""}${
        seleccionado ? " odcg-item--selected" : ""
      }`}
      style={style}
    >
      <button
        type="button"
        className="odcg-item-main"
        aria-label={`Turno ${meta.label} ${rangoCorto(item)} ${nombreCliente(item)}`}
        aria-pressed={seleccionado}
        onClick={onSelect}
      >
        <span className="odcg-item-top">
          <span className="odcg-item-dot" aria-hidden="true" />
          <span className="odcg-item-time">{rango(item)}</span>
          <SerieChip serie={item.series} />
          <span className="odcg-item-tag">{meta.label}</span>
        </span>
        <span className="odcg-item-bottom">
          <span className="odcg-item-name">{nombreCliente(item)}</span>
          {servicios && <span className="odcg-item-origin ba-item-services">{servicios}</span>}
        </span>
      </button>
      <WhatsappLink phone={item.phone} name={nombreCliente(item)} />
    </div>
  );
}

function BloqueoCard({ item, style, axis, onSelect }) {
  const motivo = item.reason || "Sin motivo";
  const cuando = item.allDay ? "Todo el día" : rangoCorto(item);
  const geometria = item.allDay ? { startMin: axis.startMin, endMin: axis.endMin } : item;
  return (
    <button
      type="button"
      className="odcg-item odcg-block"
      style={style(geometria)}
      aria-label={`Bloqueo ${motivo} ${cuando}`}
      onClick={onSelect}
    >
      <span className="odcg-item-top">
        <span className="odcg-item-dot" aria-hidden="true" />
        <span className="odcg-item-time">{item.allDay ? "Todo el día" : rango(item)}</span>
        <span className="odcg-item-tag">{item.scope === "salon" ? "Todo el salón" : "Bloqueo"}</span>
      </span>
      <span className="odcg-item-bottom">
        <span className="odcg-item-name">{motivo}</span>
      </span>
    </button>
  );
}

function CanceladoCard({ item, style }) {
  return (
    <span className="odcg-item odcg-history" style={style}>
      <span className="odcg-sr-only">{`Historial cancelado ${rangoCorto(item)}`}</span>
      <span className="odcg-item-top" aria-hidden="true">
        <span className="odcg-item-dot" />
        <span className="odcg-item-time">{rango(item)}</span>
        <span className="odcg-item-tag">Cancelado</span>
      </span>
      <span className="odcg-item-bottom" aria-hidden="true">
        <span className="odcg-item-name">{nombreCliente(item)}</span>
      </span>
    </span>
  );
}

const ahoraOffset = (date, axis, ppm) => {
  const now = dayjs();
  if (now.format("YYYY-MM-DD") !== date) return null;
  const min = now.hour() * 60 + now.minute();
  return min < axis.startMin || min > axis.endMin ? null : minuteToOffset(min, axis, ppm);
};

export default function BeautyAgendaGrid({
  date,
  model,
  mostrarCancelados = false,
  turnoSeleccionadoId = null,
  puedeCrear = true,
  onFreeSelect = () => {},
  onTurnoSelect = () => {},
  onBloqueoSelect = () => {},
}) {
  const [activaElegida, setActivaElegida] = useState(null);
  const { columns, rows, axis, rowMinutes } = model;
  const activaId = columns.some((c) => c.id === activaElegida) ? activaElegida : columns[0]?.id;

  const rowStyle = (row) => gridRowStyle(row, rowMinutes);
  const itemStyle = (item) => gridItemStyle(item, axis, rowMinutes);
  // The tail goes from the service end to the end of the preparation, clipped to the axis.
  const colaStyle = (item) => {
    const fin = item.endMin + minutosPreparacion(item);
    return itemStyle({ startMin: item.endMin, endMin: Math.min(fin, axis.endMin) });
  };
  const nowTop = ahoraOffset(date, axis, GRID_SLOT_HEIGHT_PX / rowMinutes);

  return (
    <section className="odcg ba-grid" aria-label={`Agenda del ${date}`}>
      <div className="odcg-viewport" role="region" aria-label="Agenda diaria por profesional" tabIndex="0">
        <div
          className="odcg-matrix"
          style={{
            gridTemplateColumns: `76px repeat(${columns.length}, minmax(190px, 1fr))`,
            "--odcg-slot-height": `${GRID_SLOT_HEIGHT_PX}px`,
          }}
        >
          <div className="odcg-corner" aria-hidden="true">Hora</div>
          <div className="odcg-headers" data-testid="professional-headers">
            {columns.map((col) => (
              <div
                key={col.id}
                className={`odcg-court-header${col.id === activaId ? " odcg-court-header--active" : ""}${col.active ? "" : " ba-col--muted"}`}
              >
                <h3 className="odcg-court-header-name">
                  {col.name}
                  {!col.active && <small className="ba-inactivo"> (inactivo)</small>}
                </h3>
                {columns.length > 1 && (
                  <div className="odcg-court-select-wrap">
                    <OrganizerPillSelect
                      value={activaId}
                      onChange={setActivaElegida}
                      options={columns.map((c) => ({ value: c.id, label: c.active ? c.name : `${c.name} (inactivo)` }))}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="odcg-times">
            {rows.map((row) => (
              <div
                key={row.startMin}
                className={`odcg-time${row.startMin % 60 === 0 ? " odcg-time--hour" : ""}`}
                aria-label={`Hora ${formatAxisMinute(row.startMin)}`}
                style={rowStyle(row)}
              >
                {formatAxisMinute(row.startMin)}
              </div>
            ))}
            {nowTop === null ? null : <span className="odcg-now-dot" style={{ top: nowTop }} aria-hidden="true" />}
          </div>

          {columns.map((col) => (
            <div
              key={col.id}
              className={`odcg-court${col.id === activaId ? " odcg-court--active" : ""}${col.active ? "" : " ba-col--muted"}`}
              role="group"
              aria-label={`Agenda de ${col.name}`}
            >
              <div className="odcg-slots">
                {rows.map((row) => {
                  const trabaja = dentroDeTramo(col, row);
                  const libre = puedeCrear && filaLibre(col, row, axis);
                  return (
                    <div
                      key={row.startMin}
                      className={`odcg-slot${row.startMin % 60 === 0 ? " odcg-slot--hour" : ""}${trabaja ? "" : " ba-slot--off"}`}
                      style={rowStyle(row)}
                    >
                      {libre ? (
                        <button
                          type="button"
                          className="odcg-free"
                          aria-label={`Libre ${col.name} ${formatAxisMinute(row.startMin)}`}
                          onClick={() => onFreeSelect({ professionalId: col.id, date, startMin: row.startMin })}
                        >
                          <span className="odcg-free-plus" aria-hidden="true">+</span>
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>

              <div className="odcg-items">
                {nowTop === null ? null : <span className="odcg-now-line" style={{ top: nowTop }} aria-hidden="true" />}
                {col.appointments.map((a) =>
                  minutosPreparacion(a) > 0 && a.endMin < axis.endMin ? (
                    <PreparacionCola key={`prep-${a._id}`} item={a} style={colaStyle(a)} />
                  ) : null
                )}
                {col.blocks.map((b) => (
                  <BloqueoCard
                    key={`${b._id}-${col.id}`}
                    item={b}
                    axis={axis}
                    style={itemStyle}
                    onSelect={() => onBloqueoSelect(b)}
                  />
                ))}
                {col.appointments.map((a) =>
                  a.status === "cancelled" ? (
                    mostrarCancelados ? <CanceladoCard key={a._id} item={a} style={itemStyle(a)} /> : null
                  ) : (
                    <TurnoCard
                      key={a._id}
                      item={a}
                      style={itemStyle(a)}
                      seleccionado={turnoSeleccionadoId != null && String(turnoSeleccionadoId) === String(a._id)}
                      onSelect={() => onTurnoSelect(a)}
                    />
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
